import type { ScreenContext } from "@/types";

/** Renders a registry/route token (kebab-case) as readable title text. */
export function humanizeToken(token?: string | null): string {
  if (!token) return "";
  return token
    .replace(/-+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => (w.length <= 3 ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ")
    .trim();
}

/** Builds a short "On screen: <View>" summary for a screen context. */
export function contextLabel(ctx: ScreenContext & { page: string }): string {
  const parts: string[] = [
    humanizeToken(ctx.pageTitle) || humanizeToken(ctx.page),
  ];
  if (ctx.activeView) {
    parts.push(humanizeToken(ctx.activeView));
  } else if (ctx.elements?.length) {
    parts.push(humanizeToken(ctx.elements[0]));
  }
  return parts
    .filter(
      (part, index) =>
        part &&
        parts.findIndex(
          (other) => other.toLowerCase() === part.toLowerCase(),
        ) === index,
    )
    .join(" · ");
}

/**
 * Lists the detected element IDs in human-readable form. Used to give the
 * user (and assistive tech) a legible breakdown of what context was attached.
 */
export function contextualElementsLabel(ctx: ScreenContext): string {
  const labels = (ctx.elements ?? []).map(humanizeToken).filter(Boolean);
  if (ctx.visibleRecords?.length) {
    labels.push(`${ctx.visibleRecords.length} visible or selected records`);
  }
  if (ctx.chartSummaries?.length) {
    labels.push(`${ctx.chartSummaries.length} chart summaries`);
  }
  if (ctx.formFields?.length) {
    labels.push(`${ctx.formFields.length} form fields`);
  }
  return labels.length ? labels.join(", ") : "current view";
}

/** Summarises the live key metrics, e.g. "Total Suppliers: 124, Active: 48". */
export function keyMetricsLabel(
  keyMetrics?: Record<string, string | number>,
): string {
  if (!keyMetrics) return "";
  return Object.entries(keyMetrics)
    .map(([k, v]) => `${k}: ${v}`)
    .join(" · ");
}
